import { programmeQuery } from "@common/api/maintenance-programme"
import { useHasPermission } from "@common/hooks/use-permissions"
import { useSuspenseQuery } from "@tanstack/react-query"
import { createFileRoute, useNavigate } from "@tanstack/react-router"
import { History } from "lucide-react"
import { DetailPane } from "@/components/layout/detail-panel"
import { ListEmpty } from "@/components/layout/list-empty"
import useCustomToast from "@/hooks/use-custom-toast"
import { revisionLabel } from "@/routes/_layout/maintenance-programmes/-lib/revision-label"
import { PaneHeading } from "../-components/pane-heading"
import { TaskForm } from "../-components/task-form/task-form"
import { editBlocker, useViewedRevision } from "../-lib/revision"

export const Route = createFileRoute(
  "/_layout/maintenance-programmes_/$programmeId/tasks/new",
)({
  component: NewTaskPane,
})

function NewTaskPane(): React.ReactElement {
  const { programmeId } = Route.useParams()
  const { data: programme } = useSuspenseQuery(
    programmeQuery({ programme_id: programmeId }),
  )
  const revision = useViewedRevision(programme)
  const canUpdate = useHasPermission("maintenance_programme:update")
  const navigate = useNavigate()
  const showToast = useCustomToast()
  const close = () =>
    navigate({
      to: "/maintenance-programmes/$programmeId",
      params: { programmeId },
      search: true,
    })

  return (
    <DetailPane
      title="Add task"
      heading={
        <PaneHeading
          title="Add task"
          subtitle={revision && revisionLabel(revision.number)}
        />
      }
      onClose={close}
    >
      {revision ? (
        <TaskForm
          scope={{ programme_id: programmeId, revision_id: revision.id }}
          task={undefined}
          blocked={editBlocker(programme, revision, canUpdate)}
          onSaved={(saved, another) => {
            showToast(
              `Added ${saved.task_number ?? saved.title}`,
              revisionLabel(revision.number),
              "success",
            )
            if (!another) {
              navigate({
                to: "/maintenance-programmes/$programmeId/tasks/$taskId",
                params: { programmeId, taskId: saved.id },
                search: true,
              })
            }
          }}
          onCancel={close}
        />
      ) : (
        <ListEmpty
          icon={History}
          title="No revisions"
          body="Create a revision before adding tasks."
        />
      )}
    </DetailPane>
  )
}
